import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ButtonControl, JSONEditorControl } from 'spark-core-dx/components';
import { useCoreContext, useCoreStateContext } from 'spark-core-dx/contexts';
import { AuthManager } from 'spark-core-dx/managers';
import { getAuthSessionRequestHeaders } from '../../../utilities/authSessionRequest';
import { BaseService, ItemService } from 'spark-core-dx/services';
import ConvokeCommandBar from '../../../components/convoke/ConvokeCommandBar';
import DialogControl from '../../../components/convoke/ConvokeDialogControl';
import { cleanSupportDiagnosticText, useHelpSupport } from '../../../components/helpSupport';
import './status.scss';

const AUTH_PROVIDER_LABELS = {
  0: 'AuthGuard',
  1: 'Entra ID',
  99999: 'None',
};
const CLIENT_DATABASE_PARTITION_STATES = {
  0: 'Disabled',
  1: 'Preparing',
  2: 'Ready',
  3: 'Enabled',
  4: 'Migration Failed',
  5: 'Rollback Required',
  6: 'Validated',
  7: 'Deploying',
  8: 'Deployment Failed',
};
const CONNECTION_SECTION_PRIORITY = {
  auth: 0,
  storage: 1,
  data: Number.MAX_SAFE_INTEGER,
};
const MASKED_VALUE = '**********';
const SENSITIVE_CONFIG_KEY_PATTERN = /(password|secret|token|credential|privatekey|api_?key|accesskey|accountkey|sharedaccesssignature|(^|:)sas$|pat$|aeskey|signingkey|hubticketkey|initializerkey|storag(?:e)?key|clientkey|keyring)/i;
const SUPPORT_LOG_ID_PATTERN = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;

const redactSensitiveConfig = (value, key = '') => {
  if (SENSITIVE_CONFIG_KEY_PATTERN.test(key)) {
    if (value === null || value === undefined || value === '') {
      return value;
    }
    return MASKED_VALUE;
  }

  if (Array.isArray(value)) {
    return value.map((entry) => redactSensitiveConfig(entry));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([entryKey, entryValue]) => [
        entryKey,
        redactSensitiveConfig(entryValue, entryKey),
      ]),
    );
  }

  return value;
};

const hasDisplayValue = (value) => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== undefined && value !== null;
};

const getFirstDisplayValue = (...values) => values.find((value) => hasDisplayValue(value));

const formatStatusDate = (value) => {
  if (!value) {
    return 'Not available';
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Not available' : parsed.toLocaleString();
};

export const getExpirationCountdown = (value, now = Date.now()) => {
  if (!value) return null;

  const expirationTime = new Date(value).getTime();
  const referenceTime = now instanceof Date ? now.getTime() : Number(now);
  if (!Number.isFinite(expirationTime) || !Number.isFinite(referenceTime)) return null;

  const remainingMilliseconds = expirationTime - referenceTime;
  if (remainingMilliseconds <= 0) {
    return { label: 'Expired', tone: 'error' };
  }

  const totalMinutes = Math.max(1, Math.ceil(remainingMilliseconds / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const label = hours === 0
    ? `${minutes}m left`
    : minutes === 0
      ? `${hours}h left`
      : `${hours}h ${minutes}m left`;

  return {
    label,
    tone: remainingMilliseconds <= 2 * 60 * 60 * 1000 ? 'warning' : 'success',
  };
};

const formatSummaryValue = (value, formatter) => {
  if (formatter) {
    return formatter(value);
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  if (typeof value === 'boolean') {
    return value ? 'Enabled' : 'Disabled';
  }

  return String(value);
};

const createSummaryEntry = ({ key, label, value, formatter, meta }) => {
  if (!hasDisplayValue(value)) {
    return null;
  }

  const formattedValue = formatSummaryValue(value, formatter);
  const isGuid = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(formattedValue.trim());
  const isOrgName = label.trim().toLowerCase() === 'org name';

  return {
    key,
    label,
    value: formattedValue,
    meta: hasDisplayValue(meta) ? meta : null,
    copyValue: isGuid || isOrgName ? formattedValue : null,
  };
};

const resolvePartitionState = (value) => {
  if (typeof value === 'number') {
    return CLIENT_DATABASE_PARTITION_STATES[value] ?? String(value);
  }

  const normalized = String(value ?? '').trim();
  if (!normalized) {
    return 'Disabled';
  }

  const numericState = Number(normalized);
  return Number.isFinite(numericState)
    ? CLIENT_DATABASE_PARTITION_STATES[numericState] ?? normalized
    : normalized.replace(/([a-z])([A-Z])/g, '$1 $2');
};

const buildConnectionSections = (configItem) => {
  const partitionState = resolvePartitionState(configItem?.ClientDatabasePartitionState);
  const partitionEnabled = configItem?.UseClientDatabasePartition === true && partitionState === 'Enabled';
  const hasDedicatedPartitionConnection = hasDisplayValue(configItem?.MongoDbClientPartitionConnectionString);
  const externalStorageConnections = Array.isArray(configItem?.ClientAzureFileStorageConnections)
    ? configItem.ClientAzureFileStorageConnections
    : [];
  const activeExternalStorageId = configItem?.ActiveClientAzureFileStorageConnectionId;
  const activeExternalStorage = externalStorageConnections.find((connection) => (
    String(connection?.ConnectionId ?? connection?._id ?? '').toLowerCase()
      === String(activeExternalStorageId ?? '').toLowerCase()
  ));
  const externalStorageEnabled = hasDisplayValue(activeExternalStorageId);

  return [
    {
      key: 'data',
      title: 'Data',
      entries: [
        createSummaryEntry({
          key: 'core',
          label: 'Core MongoDB',
          value: configItem?.MongoDbConnectionStringCore,
        }),
        createSummaryEntry({
          key: 'portal',
          label: 'Portal MongoDB',
          value: configItem?.MongoDbConnectionStringPortal,
        }),
        createSummaryEntry({
          key: 'database-routing',
          label: 'Database Routing',
          value: partitionEnabled ? 'Client partition' : 'Shared PlatformApp database',
          meta: partitionEnabled
            ? (hasDedicatedPartitionConnection
              ? 'Client-owned records use an isolated database through a dedicated MongoDB connection.'
              : 'Client-owned records use an isolated database on the shared MongoDB server.')
            : 'Shared routing is active for client-owned PlatformApp records.',
        }),
        createSummaryEntry({
          key: 'database-partition-state',
          label: 'Partition State',
          value: partitionState,
          meta: !partitionEnabled && partitionState !== 'Disabled'
            ? 'Partition lifecycle work exists, but runtime cutover is not active.'
            : null,
        }),
        createSummaryEntry({
          key: 'authguard',
          label: 'AuthGuard MongoDB',
          value: configItem?.MongoDbConnectionStringAuthGuard,
        }),
        createSummaryEntry({
          key: 'redis',
          label: 'Redis Cache',
          value: configItem?.RedisCacheConnectionString,
        }),
      ].filter(Boolean),
    },
    {
      key: 'auth',
      title: 'Auth',
      entries: [
        createSummaryEntry({
          key: 'auth-org-name',
          label: 'Org Name',
          value: configItem?.OrgName,
        }),
        createSummaryEntry({
          key: 'default-auth-provider',
          label: 'Default Provider',
          value: configItem?.DefaultAuthProvider,
          formatter: (value) => AUTH_PROVIDER_LABELS[value] ?? String(value),
        }),
        createSummaryEntry({
          key: 'entra-tenant',
          label: 'Tenant ID',
          value: configItem?.AZTenant,
        }),
        createSummaryEntry({
          key: 'core-auth-issuer',
          label: 'Core Auth Issuer',
          value: configItem?.CoreAuthServerIssuer,
        }),
        createSummaryEntry({
          key: 'entra-app',
          label: 'Entra App Client ID',
          value: configItem?.AZEntraIDAppClientId,
        }),
        createSummaryEntry({
          key: 'graph-app',
          label: 'Graph Client ID',
          value: configItem?.AZGraphClientId,
        }),
        createSummaryEntry({
          key: 'reporting-app',
          label: 'Reporting Client ID',
          value: configItem?.AZReportingAppClientId,
        }),
      ].filter(Boolean),
    },
    {
      key: 'storage',
      title: 'Storage',
      entries: [
        createSummaryEntry({
          key: 'queue-storage',
          label: 'Queue Storage',
          value: hasDisplayValue(configItem?.AzureQueueStorageConnectionString)
            ? 'Configured'
            : 'Not configured',
        }),
        createSummaryEntry({
          key: 'dashboard-storage',
          label: 'Dashboard Storage',
          value: hasDisplayValue(configItem?.AzureDashboardStorageConnectionString)
            ? 'Configured'
            : 'Not configured',
        }),
        createSummaryEntry({
          key: 'storage-mode',
          label: 'Storage Mode',
          value: externalStorageEnabled
            ? 'Client external Azure Blob Storage'
            : 'Platform shared Azure Blob Storage',
        }),
        createSummaryEntry({
          key: 'external-storage-enabled',
          label: 'External Storage Enabled',
          value: externalStorageEnabled,
        }),
        createSummaryEntry({
          key: 'external-storage-id',
          label: 'External Storage Connection ID',
          value: activeExternalStorageId,
        }),
        createSummaryEntry({
          key: 'external-storage-account',
          label: 'External Storage Account',
          value: activeExternalStorage?.AccountName,
        }),
        createSummaryEntry({
          key: 'external-storage-container',
          label: 'External Storage Container',
          value: activeExternalStorage?.Container,
        }),
        createSummaryEntry({
          key: 'external-storage-validation',
          label: 'External Storage Validation',
          value: externalStorageEnabled
            ? (activeExternalStorage?.IsValidated === true ? 'Validated' : 'Validation required')
            : 'Not applicable',
        }),
        createSummaryEntry({
          key: 'external-storage-validated-at',
          label: 'External Storage Last Validated',
          value: activeExternalStorage?.ValidatedUtc,
          formatter: formatStatusDate,
        }),
        createSummaryEntry({
          key: 'external-storage-count',
          label: 'Available External Connections',
          value: externalStorageConnections.length,
        }),
        createSummaryEntry({
          key: 'file-storage-account',
          label: 'Platform Blob Storage Account',
          value: configItem?.AzureFileStorageAccountName,
        }),
        createSummaryEntry({
          key: 'file-storage-container',
          label: 'Platform Blob Storage Container',
          value: configItem?.AzureFileStorageContainer,
        }),
      ].filter(Boolean),
    },
    {
      key: 'other-connections',
      title: 'Other Connections',
      entries: [
        createSummaryEntry({
          key: 'snowflake',
          label: 'Snowflake',
          value: configItem?.SnowflakeConnectionString,
        }),
        createSummaryEntry({
          key: 'buildops',
          label: 'BuildOps',
          value: configItem?.BuildOpsConnectionString,
        }),
        createSummaryEntry({
          key: 'sage',
          label: 'Sage',
          value: configItem?.SageConnectionString,
        }),
        createSummaryEntry({
          key: 'ef-1',
          label: 'Entity Framework',
          value: configItem?.EFConnectionString1,
        }),
        createSummaryEntry({
          key: 'salesforce',
          label: 'Salesforce',
          value: configItem?.SalesforceRestConnectionString,
        }),
        createSummaryEntry({
          key: 'docusign',
          label: 'DocuSign',
          value: configItem?.DocuSignConnectionString,
        }),
        createSummaryEntry({
          key: 'pipedrive',
          label: 'Pipedrive',
          value: configItem?.PipedriveConnectionString,
        }),
      ].filter(Boolean),
    },
  ]
    .filter((section) => section.entries.length > 0)
    .sort((left, right) => (
      (CONNECTION_SECTION_PRIORITY[left.key] ?? 2)
      - (CONNECTION_SECTION_PRIORITY[right.key] ?? 2)
    ));
};

const getInitialSupportLogId = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const value = new URLSearchParams(window.location.search).get('supportLogId');
  return SUPPORT_LOG_ID_PATTERN.test(value ?? '') ? value : null;
};

const getFirstResult = (response) => response?.first?.() ?? response?.[0] ?? response;

const getResultItem = (result) => (
  result?.Items?.first?.()
  ?? result?.Items?.[0]
  ?? result?.Item
  ?? result?.Value
  ?? null
);

const normalizeSupportLog = (item) => {
  if (!item) {
    return null;
  }

  let systemInfo = item.SystemInfo;
  if (typeof systemInfo === 'string') {
    try {
      systemInfo = JSON.parse(systemInfo);
    } catch {
      systemInfo = cleanSupportDiagnosticText(systemInfo, 32768);
    }
  }

  return redactSensitiveConfig({
    ...item,
    SystemInfo: systemInfo,
  });
};

const copyText = async (text) => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const input = document.createElement('textarea');
  input.value = text;
  input.setAttribute('readonly', 'readonly');
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.appendChild(input);
  input.select();
  document.execCommand('copy');
  document.body.removeChild(input);
};

const SummaryCopyAction = ({ label, value }) => {
  const [copyState, setCopyState] = useState('idle');

  useEffect(() => {
    if (copyState === 'idle') {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setCopyState('idle'), 1800);
    return () => window.clearTimeout(timeoutId);
  }, [copyState]);

  const handleCopy = async () => {
    try {
      await copyText(value);
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
  };

  const title = copyState === 'copied'
    ? `${label} copied`
    : copyState === 'error'
      ? `Could not copy ${label}`
      : `Copy ${label}`;

  return (
    <ButtonControl
      type="standard-small"
      className={`status-summary-entry__copy-action${copyState === 'error' ? ' is-error' : ''}`}
      aria-label={title}
      title={title}
      onClick={handleCopy}
    >
      <span className="material-symbols-rounded" aria-hidden="true">content_copy</span>
    </ButtonControl>
  );
};

const JsonDialog = ({ open, title, subTitle, value, emptyMessage, onClose }) => {
  const hasValue = value !== undefined && value !== null;
  const [copyState, setCopyState] = useState('idle');

  useEffect(() => {
    if (copyState === 'idle') {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setCopyState('idle');
    }, 1800);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [copyState]);

  const handleCopy = async () => {
    if (!hasValue) {
      return;
    }

    try {
      await copyText(JSON.stringify(value, null, 2));
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
  };

  const copyLabel = copyState === 'copied' ? 'Copied' : copyState === 'error' ? 'Copy Failed' : 'Copy';

  return (
    <DialogControl
      className="status-config-dialog"
      openDialog={open}
      title={title}
      subTitle={subTitle}
      okText="Close"
      excludeCancel={true}
      onOk={onClose}
      onCancel={onClose}
      onX={onClose}
    >
      <div className="status-config-dialog__content">
        <div className="status-config-dialog__actions">
          <ButtonControl
            type="standard-small"
            className={`status-config-dialog__copy-action${copyState === 'error' ? ' is-error' : ''}`}
            aria-label={copyLabel}
            title={copyLabel}
            onClick={handleCopy}
            disabled={!hasValue}
          >
            <span className="material-symbols-rounded" aria-hidden="true">content_copy</span>
          </ButtonControl>
        </div>
        {hasValue ? (
          <div className="status-json-wrap">
            <JSONEditorControl
              metadata={{ FieldName: title.replace(/\s+/g, ''), DisplayName: title }}
              value={value}
              onChange={() => {}}
            />
          </div>
        ) : (
          <div className="status-empty">{emptyMessage}</div>
        )}
      </div>
    </DialogControl>
  );
};

const AuthenticationSessionPanel = ({ sessionState }) => {
  const [countdownReferenceTime, setCountdownReferenceTime] = useState(() => Date.now());
  const statusLabel = sessionState.loading
    ? 'Checking'
    : sessionState.authenticated
      ? 'Active'
      : sessionState.error
        ? 'Check failed'
        : 'Not available';
  const statusTone = sessionState.loading
    ? 'pending'
    : sessionState.authenticated
      ? 'success'
      : 'error';
  const absoluteExpirationCountdown = getExpirationCountdown(
    sessionState.absoluteExpiresAtUtc,
    countdownReferenceTime,
  );

  useEffect(() => {
    const intervalId = window.setInterval(() => setCountdownReferenceTime(Date.now()), 60000);
    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <section className="status-panel status-auth-session convoke-panel">
      <div className="status-panel-header convoke-section-title__row convoke-section-title__row--split">
        <h2>Server Side Auth Session</h2>
        <span>Server-side refresh token</span>
      </div>

      <div className="status-auth-session__body">
        <div className="status-auth-session__facts">
          <div className="status-auth-session__fact convoke-panel convoke-metric-card">
            <span>Session state</span>
            <strong className={`status-auth-session__value status-auth-session__value--${statusTone}`}>
              {statusLabel}
            </strong>
            <small>The hosted cookie and encrypted server record are checked together.</small>
          </div>
          <div className="status-auth-session__fact convoke-panel convoke-metric-card">
            <span>Idle expiration</span>
            <strong>{formatStatusDate(sessionState.idleExpiresAtUtc)}</strong>
            <small>The session slides when a refresh succeeds.</small>
          </div>
          <div className="status-auth-session__fact convoke-panel convoke-metric-card">
            <span className="status-auth-session__fact-label-row">
              <span>Absolute expiration</span>
              {absoluteExpirationCountdown ? (
                <span
                  className={`status-auth-session__expiration-badge status-auth-session__expiration-badge--${absoluteExpirationCountdown.tone}`}
                  aria-label={`Absolute expiration ${absoluteExpirationCountdown.label}`}
                >
                  {absoluteExpirationCountdown.label}
                </span>
              ) : null}
            </span>
            <strong>{formatStatusDate(sessionState.absoluteExpiresAtUtc)}</strong>
            <small>The session cannot be extended beyond this time.</small>
          </div>
          <div className="status-auth-session__fact convoke-panel convoke-metric-card">
            <span>Last checked</span>
            <strong>{formatStatusDate(sessionState.checkedAtUtc)}</strong>
            <small>No token, cookie, or CSRF value is displayed.</small>
          </div>
        </div>

      </div>
    </section>
  );
};

const Status = () => {
  const coreContext = useCoreContext();
  const coreState = useCoreStateContext();
  const { openHelpSupport, supportRequestLoading } = useHelpSupport();
  const [snapshot, setSnapshot] = useState({
    loading: true,
    error: null,
    payload: null,
  });
  const [authSession, setAuthSession] = useState({
    loading: true,
    authenticated: false,
    idleExpiresAtUtc: null,
    absoluteExpiresAtUtc: null,
    checkedAtUtc: null,
    error: null,
  });
  const [authAction, setAuthAction] = useState({
    loading: false,
    success: null,
    message: null,
  });
  const [openConfigDialog, setOpenConfigDialog] = useState(null);
  const [requestedSupportLogId, setRequestedSupportLogId] = useState(getInitialSupportLogId);
  const [supportLog, setSupportLog] = useState({
    loading: false,
    error: null,
    item: null,
  });

  const checkAuthSession = useCallback(async () => {
    setAuthSession((previous) => ({ ...previous, loading: true, error: null }));

    try {
      const response = await window.fetch('/api/Core/AuthSession', {
        credentials: 'include',
        headers: getAuthSessionRequestHeaders({ Accept: 'application/json' }),
      });

      if (!response.ok) {
        setAuthSession({
          loading: false,
          authenticated: false,
          idleExpiresAtUtc: null,
          absoluteExpiresAtUtc: null,
          checkedAtUtc: new Date().toISOString(),
          error: response.status === 401
            ? 'No active server refresh session was found. Sign in again to create one.'
            : `The session check failed with HTTP ${response.status}.`,
        });
        return false;
      }

      const payload = await response.json();
      setAuthSession({
        loading: false,
        authenticated: payload?.authenticated === true || payload?.Authenticated === true,
        idleExpiresAtUtc: payload?.idleExpiresAtUtc ?? payload?.IdleExpiresAtUtc ?? null,
        absoluteExpiresAtUtc: payload?.absoluteExpiresAtUtc ?? payload?.AbsoluteExpiresAtUtc ?? null,
        checkedAtUtc: new Date().toISOString(),
        error: null,
      });
      return true;
    } catch {
      setAuthSession({
        loading: false,
        authenticated: false,
        idleExpiresAtUtc: null,
        absoluteExpiresAtUtc: null,
        checkedAtUtc: new Date().toISOString(),
        error: 'The server refresh session could not be checked.',
      });
      return false;
    }
  }, []);

  const refreshAccessToken = useCallback(async () => {
    setAuthAction({ loading: true, success: null, message: null });

    try {
      const result = await BaseService.forceRefreshAccessToken('status_screen_manual_refresh');
      if (!result?.Success) {
        const fatalRefreshFailure = AuthManager.isFatalRefreshFailure?.(result) ?? false;
        if (fatalRefreshFailure) {
          setAuthAction({
            loading: false,
            success: false,
            message: 'Your session ended. Redirecting to sign in.',
          });
          AuthManager.requestRegularLogin?.('status_screen_manual_refresh_failed');
          return;
        }

        setAuthAction({
          loading: false,
          success: false,
          message: 'The access token could not be refreshed. The session was not reported as successful.',
        });
        await checkAuthSession();
        return;
      }

      setAuthAction({
        loading: false,
        success: true,
        message: null,
      });
      await checkAuthSession();
    } catch {
      setAuthAction({
        loading: false,
        success: false,
        message: 'The access-token refresh request failed.',
      });
    }
  }, [checkAuthSession]);

  const closeSupportLog = useCallback(() => {
    setRequestedSupportLogId(null);
    setSupportLog({ loading: false, error: null, item: null });
    const url = new URL(window.location.href);
    url.searchParams.delete('supportLogId');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadSnapshot = async () => {
      const profileService = new ItemService('Items/Profile', coreState, coreContext, 'CORE', 30000, true);

      setSnapshot((previous) => ({
        ...previous,
        loading: true,
        error: null,
      }));

      try {
        const response = await profileService.getOperation({
          operationType: 'StatusConfigSnapshot',
          isWarning: true,
          supressAllErrors: true,
        });
        const result = response?.first?.() ?? response?.[0];
        const payload = result?.Items?.first?.() ?? result?.Items?.[0];

        if (!result?.Success) {
          throw new Error(result?.DisplayMessage ?? result?.Message?.DisplayMessage ?? 'Unable to load status snapshot.');
        }

        if (isMounted) {
          setSnapshot({
            loading: false,
            error: null,
            payload,
          });
        }
      } catch (error) {
        if (isMounted) {
          setSnapshot({
            loading: false,
            error: error?.message ?? 'Unable to load status snapshot.',
            payload: null,
          });
        }
      }
    };

    loadSnapshot();

    return () => {
      isMounted = false;
    };
  }, [coreContext, coreState]);

  useEffect(() => {
    checkAuthSession();
  }, [checkAuthSession]);

  useEffect(() => {
    if (!requestedSupportLogId) {
      return undefined;
    }

    let isMounted = true;
    const loadSupportLog = async () => {
      setSupportLog({ loading: true, error: null, item: null });
      try {
        const service = new ItemService('Items/Log', coreState, coreContext, 'CORE', 30000, true);
        const response = await service.single(requestedSupportLogId);
        const result = getFirstResult(response);
        const item = getResultItem(result);
        if (result?.Success !== true || !item) {
          throw new Error(
            result?.DisplayMessage
            ?? result?.Message?.DisplayMessage
            ?? 'The support log could not be loaded.',
          );
        }

        if (isMounted) {
          setSupportLog({ loading: false, error: null, item: normalizeSupportLog(item) });
        }
      } catch (error) {
        if (isMounted) {
          setSupportLog({
            loading: false,
            error: error?.message ?? 'The support log could not be loaded.',
            item: null,
          });
        }
      }
    };

    loadSupportLog();
    return () => {
      isMounted = false;
    };
  }, [coreContext, coreState, requestedSupportLogId]);

  const configItem = useMemo(
    () => redactSensitiveConfig(snapshot.payload?.ConfigItem ?? {}),
    [snapshot.payload?.ConfigItem],
  );
  const systemConfig = useMemo(
    () => redactSensitiveConfig(snapshot.payload?.SystemConfig ?? {}),
    [snapshot.payload?.SystemConfig],
  );
  const environmentLabel = getFirstDisplayValue(
    configItem?.EnvironmentWithLocal,
    configItem?.Environment,
    coreContext?.Launch?.Env,
  ) ?? 'Unknown';
  const connectionSections = buildConnectionSections(configItem);
  const authSessionLabel = authSession.loading
    ? 'Checking'
    : authSession.authenticated
      ? 'Active'
      : 'Unavailable';
  const authenticationError = authSession.error
    ?? (authAction.success === false ? authAction.message : null);
  const commandBarActions = useMemo(() => [
    {
      key: 'check-auth-session',
      label: 'Check Session',
      iconKey: 'refresh',
      disabled: authSession.loading || authAction.loading,
      loading: authSession.loading,
      onClick: checkAuthSession,
    },
    {
      key: 'refresh-access-token',
      label: 'Refresh Access Token',
      iconKey: 'lock',
      disabled: authSession.loading || authAction.loading || !authSession.authenticated,
      loading: authAction.loading,
      onClick: refreshAccessToken,
    },
    {
      key: 'support-help',
      label: 'Help & Support',
      iconKey: 'support_agent',
      disabled: supportRequestLoading,
      loading: supportRequestLoading,
      onClick: () => openHelpSupport({ authSession, source: 'Status' }),
    },
    {
      key: 'view-system-settings',
      label: 'System Settings',
      iconKey: 'system',
      disabled: snapshot.loading || !snapshot.payload?.SystemConfig,
      onClick: () => setOpenConfigDialog('system'),
    },
    {
      key: 'view-config',
      label: 'Config',
      iconKey: 'config',
      disabled: snapshot.loading || !snapshot.payload?.ConfigItem,
      onClick: () => setOpenConfigDialog('config'),
    },
  ], [
    authAction.loading,
    authSession,
    checkAuthSession,
    openHelpSupport,
    refreshAccessToken,
    snapshot.loading,
    snapshot.payload?.ConfigItem,
    snapshot.payload?.SystemConfig,
    supportRequestLoading,
  ]);

  return (
    <div className="status-screen convoke-surface-context">
      <header className="status-page-header">
        <div>
          <div className="status-page-header__eyebrow">System / Status</div>
          <h1>Status</h1>
          <p>Authentication health, live app configuration, and the server snapshot for the app you are currently using.</p>
        </div>
        <div className="status-page-header__status" aria-label="Authentication session status">
          <span>
            <span className="status-page-header__status-label">Authentication Session</span>
            <span>{authSessionLabel}</span>
          </span>
          <span>Last Check: {formatStatusDate(authSession.checkedAtUtc)}</span>
          {authAction.loading ? <span className="is-pending">Refreshing access token</span> : null}
        </div>
      </header>

      <ConvokeCommandBar
        actions={commandBarActions}
        ariaLabel="Status actions"
        className="status-command-bar convoke-command-bar--screen"
        useSparkButtons
      />

      <JsonDialog
        open={openConfigDialog === 'system'}
        title="System Settings"
        subTitle="Runtime settings returned by the current Web API host. Sensitive values remain redacted."
        value={systemConfig}
        emptyMessage="SystemConfig has not been loaded yet."
        onClose={() => setOpenConfigDialog(null)}
      />
      <JsonDialog
        open={openConfigDialog === 'config'}
        title="Config"
        subTitle="The active application configuration snapshot for this environment."
        value={configItem}
        emptyMessage="ConfigItem has not been loaded yet."
        onClose={() => setOpenConfigDialog(null)}
      />
      <JsonDialog
        open={Boolean(requestedSupportLogId)}
        title="Support Log"
        subTitle={requestedSupportLogId ? `Report ${requestedSupportLogId}` : 'Support report'}
        value={supportLog.item}
        emptyMessage={supportLog.loading
          ? 'Loading support log...'
          : supportLog.error ?? 'The support log is unavailable.'}
        onClose={closeSupportLog}
      />

      <div className="status-content">
        <div className="status-scroll-content">
          {authenticationError ? (
            <div className="status-action-note status-action-note--error" role="alert">
              {authenticationError}
            </div>
          ) : null}

          <AuthenticationSessionPanel
            sessionState={authSession}
          />

          <section className="status-panel status-summary convoke-panel">
            <div className="status-panel-header convoke-section-title__row convoke-section-title__row--split">
              <h2>{`Connection Summary for ${environmentLabel}`}</h2>
              <span>{snapshot.loading ? 'Loading...' : `${connectionSections.length} group(s)`}</span>
            </div>

            <div className="status-summary-scroll">
              <div className="status-summary-grid">
                {connectionSections.length > 0 ? (
                  connectionSections.map((section) => (
                    <section key={section.key} className="status-summary-section convoke-panel">
                      <div className="status-summary-section__header">
                        <span className="status-summary-section__title">{section.title}</span>
                        <span className="status-summary-section__count">{section.entries.length}</span>
                      </div>
                      <div className="status-summary-section__entries">
                        {section.entries.map((entry) => (
                          <div key={entry.key} className="status-summary-entry">
                            <div className="status-summary-entry__header">
                              <span className="status-summary-entry__label">{entry.label}</span>
                              {entry.copyValue ? (
                                <SummaryCopyAction label={entry.label} value={entry.copyValue} />
                              ) : null}
                            </div>
                            <div className="status-summary-entry__value-wrap">
                              <span className="status-summary-entry__value">{entry.value}</span>
                            </div>
                            {entry.meta ? (
                              <span className="status-summary-entry__meta">{entry.meta}</span>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </section>
                  ))
                ) : (
                  <div className="status-empty">
                    {snapshot.loading
                      ? 'Loading connection summary...'
                      : 'No connection-oriented settings were found in the current config snapshot.'}
                  </div>
                )}
              </div>
            </div>

          </section>

        </div>
      </div>
    </div>
  );
};

export default Status;
